import { useState } from 'react';
import {
  RECURRING_FREQUENCIES,
  type CreateRecurringEntryInput,
  type RecurringEntryWithLines,
  type RecurringFrequency,
} from '@accounting/shared';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../accounts/AccountPicker';
import { formatCurrency, todayIso } from '../../lib/formatters';

interface LineRow {
  accountId: string;
  debit: string;
  credit: string;
  memo: string;
}

function blankLine(): LineRow {
  return { accountId: '', debit: '', credit: '', memo: '' };
}

export function RecurringEntryForm({
  companyId,
  initial,
  submitLabel,
  pending,
  errorMessage,
  onSubmit,
}: {
  companyId: string;
  initial?: RecurringEntryWithLines;
  submitLabel: string;
  pending: boolean;
  errorMessage?: string;
  onSubmit: (input: CreateRecurringEntryInput) => void;
}) {
  const { data: accounts = [] } = useAccounts(companyId);

  const [name, setName] = useState(initial?.name ?? '');
  const [frequency, setFrequency] = useState<RecurringFrequency>(initial?.frequency ?? 'monthly');
  const [interval, setIntervalValue] = useState(String(initial?.interval ?? 1));
  const [dayOfMonth, setDayOfMonth] = useState(String(initial?.dayOfMonth ?? 1));
  const [startDate, setStartDate] = useState(initial?.startDate ?? todayIso());
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [maxRuns, setMaxRuns] = useState(initial?.maxRuns != null ? String(initial.maxRuns) : '');
  const [memoTemplate, setMemoTemplate] = useState(initial?.memoTemplate ?? '');
  const [reference, setReference] = useState(initial?.reference ?? '');
  const [lines, setLines] = useState<LineRow[]>(
    initial?.lines.length
      ? initial.lines.map((l) => ({ accountId: l.accountId, debit: l.debit, credit: l.credit, memo: l.memo ?? '' }))
      : [blankLine(), blankLine()],
  );

  const setLine = (i: number, patch: Partial<LineRow>) =>
    setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.005;
  const usableLines = lines.filter(
    (l) => l.accountId && ((parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0),
  );
  const valid = name.trim() && balanced && usableLines.length >= 2;

  const submit = () => {
    const input: CreateRecurringEntryInput = {
      name: name.trim(),
      frequency,
      interval: Number(interval) || 1,
      dayOfMonth: frequency === 'weekly' ? null : Math.min(Math.max(Number(dayOfMonth) || 1, 1), 28),
      startDate,
      endDate: endDate || null,
      maxRuns: maxRuns ? Number(maxRuns) : null,
      memoTemplate: memoTemplate.trim() || null,
      reference: reference.trim() || null,
      lines: usableLines.map((l) => ({
        accountId: l.accountId,
        debit: String(parseFloat(l.debit) || 0),
        credit: String(parseFloat(l.credit) || 0),
        memo: l.memo.trim() || null,
      })),
    };
    onSubmit(input);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16, padding: 16, maxWidth: 760 }}>
        <div className="form-grid">
          <label htmlFor="r-name">Name</label>
          <input id="r-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="e.g. Monthly depreciation" />

          <label htmlFor="r-freq">Frequency</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select id="r-freq" value={frequency} onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}>
              {RECURRING_FREQUENCIES.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <span style={{ fontSize: 13 }}>every</span>
            <input style={{ width: 56 }} inputMode="numeric" value={interval} onChange={(e) => setIntervalValue(e.target.value)} />
            <span style={{ fontSize: 13 }}>{frequency === 'weekly' ? 'week(s)' : frequency === 'yearly' ? 'year(s)' : frequency === 'quarterly' ? 'quarter(s)' : 'month(s)'}</span>
            {frequency !== 'weekly' && (
              <>
                <span style={{ fontSize: 13 }}>on day</span>
                <input style={{ width: 56 }} inputMode="numeric" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} title="1-28" />
              </>
            )}
          </div>

          <label htmlFor="r-start">Start date</label>
          <input id="r-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ maxWidth: 200 }} />

          <label htmlFor="r-end">End date</label>
          <input id="r-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ maxWidth: 200 }} placeholder="(optional)" />

          <label htmlFor="r-max">Max runs</label>
          <input id="r-max" inputMode="numeric" value={maxRuns} onChange={(e) => setMaxRuns(e.target.value)} placeholder="(optional — blank = unlimited)" style={{ maxWidth: 200 }} />

          <label htmlFor="r-memo">Memo template</label>
          <input id="r-memo" value={memoTemplate} onChange={(e) => setMemoTemplate(e.target.value)} maxLength={500} placeholder="e.g. Depreciation {YYYY}-{MM}" />

          <label htmlFor="r-ref">Reference</label>
          <input id="r-ref" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={100} placeholder="(optional)" style={{ maxWidth: 280 }} />
        </div>
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Memo placeholders: <code>{'{YYYY}'}</code> <code>{'{MM}'}</code> <code>{'{MONTH}'}</code> <code>{'{QUARTER}'}</code> — filled with each run's date.
        </p>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Lines</h2>
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Account</th>
              <th>Memo</th>
              <th style={{ textAlign: 'right', width: 130 }}>Debit</th>
              <th style={{ textAlign: 'right', width: 130 }}>Credit</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <AccountPicker
                    accounts={accounts}
                    value={l.accountId}
                    onChange={(id) => setLine(i, { accountId: id })}
                    predicate={(a) => a.isActive && a.allowDirectPosting}
                  />
                </td>
                <td><input value={l.memo} onChange={(e) => setLine(i, { memo: e.target.value })} style={{ width: '100%' }} /></td>
                <td><input inputMode="decimal" value={l.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })} style={{ width: '100%', textAlign: 'right' }} placeholder="0.00" /></td>
                <td><input inputMode="decimal" value={l.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })} style={{ width: '100%', textAlign: 'right' }} placeholder="0.00" /></td>
                <td><button className="btn-danger" onClick={() => removeLine(i)} disabled={lines.length <= 2}>×</button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={2} style={{ textAlign: 'right' }}>Totals</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(totalDebit.toFixed(2))}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(totalCredit.toFixed(2))}</td>
              <td></td>
            </tr>
            <tr>
              <td colSpan={5} style={{ textAlign: 'right', fontSize: 13 }} className={balanced ? '' : 'field-error'}>
                {balanced ? 'Balanced ✓' : `Out of balance by ${formatCurrency(Math.abs(totalDebit - totalCredit).toFixed(2))}`}
              </td>
            </tr>
          </tfoot>
        </table>
        <button className="btn-secondary" onClick={addLine}>+ Add line</button>

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn-primary" onClick={submit} disabled={!valid || pending}>
            {pending ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
